import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const topics = [
  { label: "Empresa y estrategia", desc: "Modelo de negocio, ventaja competitiva, decisiones que ordenan una compañía." },
  { label: "IA y organización", desc: "Cómo la inteligencia artificial rediseña el trabajo, los procesos y el rol del directivo." },
  { label: "Empleo y transformación", desc: "El futuro del trabajo desde la perspectiva de quien tiene que gestionarlo, no solo comentarlo." },
  { label: "Atención al cliente y confianza", desc: "Por qué la confianza es el activo más rentable en mercados saturados." },
  { label: "Geopolítica empresarial", desc: "Cómo el poder, la regulación y los grandes movimientos globales afectan a las empresas." },
  { label: "Fundraising y narrativa", desc: "Storytelling corporativo, relato para inversores y comunicación estratégica que funciona." },
];

const conversations = [
  "Directivos y comités de dirección",
  "Fundadores y scaleups",
  "Escuelas de negocio y universidades",
  "Medios económicos y tecnológicos",
  "Inversores y fondos de capital",
  "Corporates en transformación",
];

export default function About() {
  return (
    <div className="pt-16 md:pt-20">

      {/* ── HEADER ── */}
      <section data-testid="about-header" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-start">
            <div className="animate-fade-slide-up">
              <p className="label-mono text-muted-foreground mb-6">Sobre mí</p>
              <h1 className="font-serif text-4xl sm:text-5xl font-normal leading-[1.1] mb-8 tracking-tight">
                CEO que construye empresa. Fundador que escribe sobre lo que aprende.
              </h1>
              <p className="text-base md:text-lg text-muted-foreground leading-relaxed mb-6">
                Soy Eduardo Delgado Lobejón. CEO y cofundador de Roams. Me obsesiona entender
                cómo la tecnología cambia el trabajo, el poder y la confianza, y cómo construir
                empresas que resistan esos cambios en lugar de ignorarlos.
              </p>
              <p className="text-base text-muted-foreground leading-relaxed">
                No escribo desde la consultoría. Escribo desde la trinchera: operación real, equipo,
                crecimiento, regulación, fundraising, clientes y aprendizaje acumulado en un mercado
                complejo y profundamente humano.
              </p>
            </div>
            <div className="relative">
              <img
                src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/j79m289m_IMG_0464.jpeg"
                alt="Eduardo Delgado en panel"
                data-testid="about-main-photo"
                className="w-full object-cover max-h-[520px] grayscale-[10%]"
              />
            </div>
          </div>
        </div>
      </section>

      {/* ── QUÉ HA CONSTRUIDO ── */}
      <section data-testid="about-roams-section" className="py-24 border-b border-border bg-card">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="max-w-3xl">
            <p className="label-mono text-muted-foreground mb-6">Qué ha construido</p>
            <h2 className="font-serif text-3xl md:text-4xl font-normal mb-8 leading-tight">
              Roams: empresa tecnológica en servicios esenciales.
            </h2>
            <div className="space-y-5 text-sm md:text-base text-muted-foreground leading-relaxed">
              <p>
                Roams opera en un mercado difícil: servicios esenciales (telefonía, energía, seguros) donde
                la competencia es feroz, el cliente está saturado de oferta y la confianza se ha deteriorado
                de forma sistemática. No es un mercado cómodo para construir. Es exactamente el tipo de mercado
                donde se aprende de verdad.
              </p>
              <p>
                La tesis central de Roams es que en mercados saturados la ventaja competitiva duradera no está en
                el precio ni en el canal, sino en la confianza. Primero humanizar: construir un servicio que
                funcione de verdad cuando el cliente tiene un problema. Luego digitalizar: usar la tecnología
                para que ese servicio sea más eficiente, más escalable y más preciso.
              </p>
              <p>
                Llevamos años integrando IA de forma operativa, no como estrategia de comunicación. En atención
                al cliente, en análisis de datos, en procesos internos. Siempre al servicio de algo concreto,
                nunca como maquillaje tecnológico.
              </p>
              <p>
                Construimos desde Palencia con ambición nacional e internacional. Eso da personalidad, rigor
                y una capa de singularidad que los fundadores de capital no tienen que ganarse.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ── TEMAS ── */}
      <section data-testid="about-topics-section" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="label-mono text-muted-foreground mb-6">Sobre qué escribe y habla</p>
          <h2 className="font-serif text-3xl md:text-4xl font-normal mb-12 leading-tight max-w-lg">
            Seis territorios donde empresa y realidad se tocan.
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {topics.map((t, i) => (
              <div key={t.label} data-testid={`topic-item-${i}`} className="border-t border-border pt-6">
                <span className="font-mono text-2xl text-border font-light">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-serif text-lg mt-3 mb-2">{t.label}</p>
                <p className="text-xs text-muted-foreground leading-relaxed">{t.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FOTOS ADICIONALES ── */}
      <section className="py-12 border-b border-border bg-card overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <img
              src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/ch2r0wmu_IMG_0396%202.jpeg"
              alt="Premio MWC"
              className="w-full h-48 object-cover grayscale-[15%]"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/kkjmxjy8_7fea03f9-f6d7-4502-a638-0134683785f7.jpg"
              alt="Castilla y León Emprende"
              className="w-full h-48 object-cover grayscale-[15%]"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/z7vx5e2w_IMG_0394%202.jpeg"
              alt="MWC Barcelona"
              className="w-full h-48 object-cover grayscale-[15%]"
            />
            <img
              src="https://customer-assets.emergentagent.com/job_ai-strategy-real/artifacts/hzqpgdt8__DSC4824.jpg"
              alt="Eduardo en evento"
              className="w-full h-48 object-cover object-top grayscale-[15%]"
            />
          </div>
        </div>
      </section>

      {/* ── CONVERSACIONES ── */}
      <section data-testid="about-conversations-section" className="py-24 border-b border-border">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
            <div>
              <p className="label-mono text-muted-foreground mb-6">Con quién habla</p>
              <h2 className="font-serif text-3xl font-normal mb-8 leading-tight">
                Conversaciones que busca.
              </h2>
              <ul className="space-y-4">
                {conversations.map((c) => (
                  <li key={c} className="flex items-center gap-3 text-sm text-muted-foreground">
                    <span className="w-1 h-1 rounded-full bg-primary shrink-0" />
                    {c}
                  </li>
                ))}
              </ul>
            </div>
            {/* Cierre */}
            <div className="flex flex-col justify-between">
              <blockquote className="font-serif text-2xl md:text-3xl font-normal italic leading-snug text-foreground/80 border-l-2 border-primary pl-6">
                "Aquí hay criterio útil, experiencia aplicada y una forma de pensar que puede ayudar a otros a tomar mejores decisiones."
              </blockquote>
              <div className="flex gap-4 mt-10">
                <Link
                  to="/ponencias"
                  data-testid="about-speaking-cta"
                  className="label-mono bg-primary text-primary-foreground px-7 py-3.5 hover:bg-primary/90 transition-colors"
                >
                  Ver ponencias
                </Link>
                <Link
                  to="/contacto"
                  data-testid="about-contact-cta"
                  className="label-mono border border-border px-7 py-3.5 hover:border-primary hover:text-primary transition-colors flex items-center gap-2"
                >
                  Contactar <ArrowRight size={13} />
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
